import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowUpCircle, ArrowDownCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TX_LABELS = {
  depot: 'Dépôt', retrait: 'Retrait', bonus: 'Bonus', transfert: 'Transfert',
  transfert_credit: 'Transfert', interet: 'Intérêt', placement: 'Placement', frais: 'Frais',
  souscription: 'Souscription', retrait_livret: 'Retrait livret', ajout_livret: 'Ajout livret'
};

const STATUS_COLORS = {
  valide: 'bg-green-500', approved: 'bg-green-500', completed: 'bg-green-500',
  pending: 'bg-orange-500', en_attente: 'bg-orange-500',
  annule: 'bg-red-500/20 text-red-400', rejected: 'bg-red-500/20 text-red-400',
  conteste: 'bg-amber-500/20 text-amber-400',
};

const PAGE_SIZES = [10, 20, 50, 100, 500];

export default function AdminTransactions() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const [searchParams] = useSearchParams();
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [offices, setOffices] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    tx_type: searchParams.get('tx_type') || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    office_id: '',
    analyst_id: ''
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_ops' || user?.role === 'bureau';
  const isAnalyst = user?.role === 'analyst';
  const showOfficeFilter = isAdmin || isAnalyst;
  const showAnalystFilter = isAdmin || isAnalyst;

  // Fetch offices and analysts for filters
  useEffect(() => {
    if (!showOfficeFilter) return;
    axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOffices(res.data || []))
      .catch(() => {});
  }, [token, showOfficeFilter]);

  useEffect(() => {
    if (!showAnalystFilter) return;
    axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data?.analysts || [];
        setAnalysts(list);
      })
      .catch(() => {});
  }, [token, showAnalystFilter]);

  // Filter analysts by selected office
  const filteredAnalysts = filters.office_id
    ? analysts.filter(a => a.office_id === filters.office_id)
    : analysts;

  const fetchTransactions = useCallback(async (p = page, size = pageSize) => {
    setLoading(true);
    try {
      const params = { page: p, limit: size };
      if (filters.status) params.status = filters.status;
      if (filters.tx_type) params.tx_type = filters.tx_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.office_id) params.office_id = filters.office_id;
      if (filters.analyst_id) params.analyst_id = filters.analyst_id;

      const res = await axios.get(`${API}/transactions-all`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
      setPage(p);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [token, filters, pageSize]);

  useEffect(() => { fetchTransactions(1, pageSize); }, [filters, pageSize]);

  const updateStatus = async (tx, newStatus) => {
    try {
      await axios.put(`${API}/transactions/${tx.client_id}/${tx.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Transaction ${newStatus === 'valide' ? 'validée' : newStatus === 'annule' ? 'annulée' : newStatus}`);
      fetchTransactions(page, pageSize);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-transactions-page">
        <h1 className="text-2xl font-bold">Transactions</h1>

        {/* Filters */}
        <Card className="bg-card/80">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              {showOfficeFilter && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Bureau</label>
                  <Select value={filters.office_id || 'all'} onValueChange={(v) => setFilters(p => ({...p, office_id: v === 'all' ? '' : v}))}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-office"><SelectValue placeholder="Tous les bureaux" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les bureaux</SelectItem>
                      {offices.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showAnalystFilter && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Analyste</label>
                  <Select value={filters.analyst_id || 'all'} onValueChange={(v) => setFilters(p => ({...p, analyst_id: v === 'all' ? '' : v}))}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-analyst"><SelectValue placeholder="Tous les analystes" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les analystes</SelectItem>
                      {filteredAnalysts.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Statut</label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(p => ({...p, status: v === 'all' ? '' : v}))}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="valide">Validé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                    <SelectItem value="conteste">Contesté</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Type</label>
                <Select value={filters.tx_type || 'all'} onValueChange={(v) => setFilters(p => ({...p, tx_type: v === 'all' ? '' : v}))}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="depot">Dépôt</SelectItem>
                    <SelectItem value="retrait">Retrait</SelectItem>
                    <SelectItem value="transfert">Transfert</SelectItem>
                    <SelectItem value="interet">Intérêt</SelectItem>
                    <SelectItem value="bonus">Bonus</SelectItem>
                    <SelectItem value="placement">Placement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Du</label>
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters(p => ({...p, start_date: e.target.value}))} className="w-36 h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Au</label>
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters(p => ({...p, end_date: e.target.value}))} className="w-36 h-8 text-xs" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setFilters({ status: '', tx_type: '', start_date: '', end_date: '', office_id: '', analyst_id: '' })}>
                Effacer
              </Button>
              <div className="ml-auto">
                <Badge variant="outline">{total} transaction{total > 1 ? 's' : ''}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card/80">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-2">Client</th>
                    <th className="text-left py-3 px-2">Type</th>
                    <th className="text-left py-3 px-2">Description</th>
                    <th className="text-right py-3 px-2">Montant</th>
                    <th className="text-center py-3 px-2">Statut</th>
                    <th className="text-center py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-8"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                  ) : transactions.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Aucune transaction</td></tr>
                  ) : transactions.map(tx => {
                    const isCredit = ['depot', 'bonus', 'interet', 'transfert_credit'].includes(tx.transaction_type);
                    return (
                      <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`tx-row-${tx.id}`}>
                        <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <Link to={`/admin/clients/${tx.client_id}`} className="hover:underline text-primary text-xs font-medium">
                            {tx.client_name || tx.client_id?.slice(0, 8)}
                          </Link>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            {isCredit ? <ArrowDownCircle className="w-3 h-3 text-green-500" /> : <ArrowUpCircle className="w-3 h-3 text-red-500" />}
                            <span className="text-xs">{TX_LABELS[tx.transaction_type] || tx.transaction_type}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-xs text-muted-foreground max-w-[200px] truncate">{tx.description || '-'}</td>
                        <td className={`py-3 px-2 text-right font-mono text-xs font-semibold ${isCredit ? 'text-green-500' : 'text-red-500'}`}>
                          {isCredit ? '+' : '-'}{Math.abs(tx.amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={`text-[10px] ${STATUS_COLORS[tx.status] || 'bg-muted'}`}>
                            {tx.status === 'valide' ? 'Validé' : tx.status === 'pending' ? 'En attente' : tx.status === 'annule' ? 'Annulé' : tx.status === 'conteste' ? 'Contesté' : tx.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          {['souscription', 'retrait_livret', 'ajout_livret'].includes(tx.transaction_type) ? (
                            <Link to={`/admin/clients/${tx.client_id}?tab=transactions`}>
                              <Button size="sm" variant="outline" className="h-7 text-[10px]">Voir</Button>
                            </Link>
                          ) : (
                            <select
                              value={tx.status}
                              onChange={(e) => updateStatus(tx, e.target.value)}
                              className="h-7 text-[10px] rounded border border-input bg-background px-1"
                              data-testid={`status-select-${tx.id}`}
                            >
                              <option value="pending">En attente</option>
                              <option value="valide">Validé</option>
                              <option value="annule">Annulé</option>
                              <option value="conteste">Contesté</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  {total} résultat{total > 1 ? 's' : ''} — Page {page}/{totalPages || 1}
                </p>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-7 text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map(s => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">par page</span>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1" data-testid="pagination-controls">
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => fetchTransactions(page - 1, pageSize)}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  {getPageNumbers().map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className="px-1 text-xs text-muted-foreground">...</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-7 w-7 text-xs"
                        style={p === page ? { backgroundColor: primaryColor } : {}}
                        onClick={() => fetchTransactions(p, pageSize)}
                        data-testid={`page-${p}`}
                      >
                        {p}
                      </Button>
                    )
                  )}
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => fetchTransactions(page + 1, pageSize)}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
